import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Heart, Zap } from "lucide-react";
import { useCart } from "@/contexts/CartContext";
import GlossyIcon from "@/components/GlossyIcon";
import { useWishlist } from "@/contexts/WishlistContext";
import { toast } from "sonner";
import { getFirstImage, getAllImages, optimizedImage, type Product } from "@/hooks/useProducts";
import plymhairLogo from "@/assets/plymhair-logo.svg";
import cleanDescription from "@/lib/cleanDescription";
import OrderOfflineButton from "@/components/OrderOfflineButton";
import { OUT_OF_STOCK_LABEL } from "@/lib/socialLinks";

interface ShopProductCardProps {
  product: Product;
  onQuickView: (product: Product) => void;
  index?: number;
  listView?: boolean;
}

const ShopProductCard = ({ product, onQuickView, index = 0, listView = false }: ShopProductCardProps) => {
  const { addItem } = useCart();
  const { isInWishlist, toggleWishlist } = useWishlist();
  const navigate = useNavigate();
  const [hovered, setHovered] = useState(false);

  const handleBuyNow = (e?: React.MouseEvent) => {
    e?.preventDefault();
    addItem(product);
    navigate("/checkout");
  };

  const hasSale = product.sale_price && product.regular_price && product.sale_price < product.regular_price;
  const outOfStock = !product.in_stock;

  const images = getAllImages(product.images);
  const rawPrimary = images[0] || "/placeholder.svg";
  const rawSecondary = images.length > 1 ? images[1] : null;
  const primaryImage = listView ? optimizedImage(rawPrimary, 320) : optimizedImage(rawPrimary, 480);
  const secondaryImage = rawSecondary ? (listView ? optimizedImage(rawSecondary, 320) : optimizedImage(rawSecondary, 480)) : null;

  if (listView) {
    return (
      <div
        className="group flex gap-4 animate-in fade-in slide-in-from-bottom-3 fill-mode-both border-b border-border pb-4"
        style={{ animationDelay: `${index * 60}ms`, animationDuration: "400ms" }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <Link
          to={`/product/${product.slug}`}
          className="relative shrink-0 w-28 h-28 sm:w-36 sm:h-36 overflow-hidden rounded-lg"
        >
          <img
            src={hovered && secondaryImage ? secondaryImage : primaryImage}
            alt={`${product.name}${product.categories ? ' – ' + product.categories.split(',')[0]?.split('>').pop()?.trim() : ''} – Plymhair`}
            loading="lazy"
            className={`w-full h-full object-cover transition-all duration-500 ease-out ${
              outOfStock ? "opacity-50 saturate-[0.3]" : ""
            }`}
          />
          {outOfStock && (
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="font-display text-[9px] uppercase tracking-[0.25em] text-primary bg-background/95 border border-primary/40 backdrop-blur-md px-3 py-1 rounded-full">
                {OUT_OF_STOCK_LABEL}
              </span>
            </div>
          )}
        </Link>

        <div className="flex-1 flex flex-col justify-center min-w-0">
          <Link to={`/product/${product.slug}`}>
            <h3 className="font-body text-sm font-normal text-foreground leading-relaxed line-clamp-2 group-hover:text-primary transition-colors duration-300">
              {product.name}
            </h3>
          </Link>
          {product.short_description && (
            <p className="text-xs text-muted-foreground line-clamp-2 mt-1 font-body">{cleanDescription(product.short_description)}</p>
          )}
          <div className="flex items-baseline gap-2 mt-1.5">
            {hasSale ? (
              <>
                <span className="font-body font-medium text-foreground text-sm">
                  £{product.sale_price!.toFixed(2)}
                </span>
                <span className="font-body text-muted-foreground/60 line-through text-xs">
                  £{product.regular_price!.toFixed(2)}
                </span>
              </>
            ) : product.regular_price ? (
              <span className="font-body font-medium text-foreground text-sm">
                £{product.regular_price.toFixed(2)}
              </span>
            ) : (
              <span className="font-body text-muted-foreground text-sm">—</span>
            )}
          </div>
          <div className="flex items-center gap-2 mt-2.5">
            {!outOfStock ? (
              <>
                <button
                  onClick={() => onQuickView(product)}
                  className="text-[10px] font-body font-medium uppercase tracking-[0.1em] border border-border px-3 py-1.5 rounded-md hover:bg-secondary transition-colors"
                >
                  Quick View
                </button>
                <button
                  onClick={() => {
                    addItem(product);
                    toast.success(`${product.name} added to cart`);
                  }}
                  className="text-[10px] font-body font-medium uppercase tracking-[0.1em] bg-foreground text-background px-3 py-1.5 rounded-md hover:bg-foreground/90 transition-colors"
                >
                  Add to Bag
                </button>
                <button
                  onClick={() => handleBuyNow()}
                  className="text-[10px] font-body font-medium uppercase tracking-[0.1em] bg-primary text-primary-foreground px-3 py-1.5 rounded-md hover:bg-primary/90 transition-colors flex items-center gap-1"
                >
                  <Zap className="w-3 h-3" /> Buy Now
                </button>
              </>
            ) : (
              <OrderOfflineButton productName={product.name} productSku={product.sku} variant="compact" />
            )}
            <button
              onClick={() => toggleWishlist(product.id)}
              aria-label="Add to wishlist"
              className={`ml-auto w-7 h-7 rounded-full flex items-center justify-center transition-all duration-200 ${
                isInWishlist(product.id)
                  ? "bg-primary text-primary-foreground"
                  : "border border-border hover:bg-secondary"
              }`}
            >
              <Heart className={`w-3 h-3 ${isInWishlist(product.id) ? "fill-current" : "text-icon-love"}`} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="group animate-in fade-in slide-in-from-bottom-3 fill-mode-both"
      style={{ animationDelay: `${index * 60}ms`, animationDuration: "400ms" }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Image container */}
      <Link
        to={`/product/${product.slug}`}
        className="relative block overflow-hidden aspect-[3/4] rounded-lg"
      >
        {/* Primary image */}
        <img
          src={primaryImage}
          alt={`${product.name}${product.categories ? ' – ' + product.categories.split(',')[0]?.split('>').pop()?.trim() : ''} – Plymhair`}
          loading="lazy"
          className={`absolute inset-0 w-full h-full object-cover transition-all duration-[800ms] ease-out group-hover:scale-[1.04] ${
            outOfStock ? "opacity-50 saturate-[0.3]" : ""
          } ${secondaryImage && hovered ? "opacity-0" : "opacity-100"}`}
        />
        {/* Secondary image (swap on hover) */}
        {secondaryImage && (
          <img
            src={secondaryImage}
            alt={`${product.name} alternate view – Plymhair`}
            loading="lazy"
            className={`absolute inset-0 w-full h-full object-cover transition-all duration-[800ms] ease-out group-hover:scale-[1.04] ${
              hovered ? "opacity-100" : "opacity-0"
            }`}
          />
        )}

        {/* Logo watermark — top-left */}
        <div className="absolute top-2.5 left-2.5 w-8 h-8 rounded-full bg-background/80 backdrop-blur-sm flex items-center justify-center shadow-sm">
          <img src={plymhairLogo} alt="Plymhair and Extensions boutique logo" className="w-5 h-5 object-contain" />
        </div>

        {/* Sale badge */}
        {hasSale && product.regular_price && product.sale_price && (
          <span className="absolute top-2.5 left-12 bg-primary text-primary-foreground text-[9px] font-body font-semibold uppercase tracking-[0.1em] px-2 py-0.5 rounded-full shadow-sm">
            -{Math.round(((product.regular_price - product.sale_price) / product.regular_price) * 100)}%
          </span>
        )}

        {/* Wishlist */}
        <button
          onClick={(e) => {
            e.preventDefault();
            toggleWishlist(product.id);
          }}
          aria-label="Add to wishlist"
          className={`absolute top-2.5 right-2.5 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 ${
            isInWishlist(product.id)
              ? "bg-primary text-primary-foreground scale-100"
              : "bg-foreground/50 backdrop-blur-md border border-foreground/10 text-primary-foreground opacity-100 sm:opacity-0 sm:group-hover:opacity-100 hover:bg-foreground/70"
          }`}
        >
          <Heart className={`w-3.5 h-3.5 ${isInWishlist(product.id) ? "fill-current" : "text-icon-love"}`} />
        </button>

        {/* Sold out label */}
        {outOfStock && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-background/40 backdrop-blur-[1px]">
            <span className="font-display text-[10px] uppercase tracking-[0.3em] text-primary bg-background/95 border border-primary/40 backdrop-blur-md px-4 py-1.5 rounded-full shadow-sm">
              {OUT_OF_STOCK_LABEL}
            </span>
            <div onClick={(e) => e.preventDefault()}>
              <OrderOfflineButton productName={product.name} productSku={product.sku} variant="compact" />
            </div>
          </div>
        )}


        {/* Glassmorphism action overlay */}
        {!outOfStock && (
          <div className="absolute bottom-0 left-0 right-0 translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]">
            <div className="mx-2 mb-2 rounded-lg bg-foreground/70 backdrop-blur-xl border border-foreground/10 shadow-lg">
              <div className="flex flex-col sm:flex-row items-center justify-center gap-1 px-2 sm:px-3 py-2 sm:py-2.5">
                <div className="flex items-center w-full sm:w-auto gap-1">
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      onQuickView(product);
                    }}
                    className="flex-1 flex items-center justify-center gap-1 text-primary-foreground text-[10px] font-body font-medium uppercase tracking-[0.12em] py-1 rounded-md hover:bg-primary-foreground/15 transition-colors"
                    title="Quick View"
                  >
                     <GlossyIcon name="quick-view" size={14} />
                    View
                  </button>
                  <div className="w-px h-4 bg-primary-foreground/25" />
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      addItem(product);
                      toast.success(`${product.name} added to cart`);
                    }}
                    className="flex-1 flex items-center justify-center gap-1 text-primary-foreground text-[10px] font-body font-medium uppercase tracking-[0.12em] py-1 rounded-md hover:bg-primary-foreground/15 transition-colors"
                    title="Add to Bag"
                  >
                     <GlossyIcon name="cart" size={14} />
                     Add
                  </button>
                </div>
                <button
                  onClick={(e) => handleBuyNow(e)}
                  className="w-full sm:w-auto flex items-center justify-center gap-1 bg-primary/90 text-primary-foreground text-[10px] font-body font-medium uppercase tracking-[0.12em] py-1.5 sm:py-1 px-3 rounded-md hover:bg-primary transition-colors"
                  title="Buy Now"
                >
                   <Zap className="w-3 h-3" />
                   Buy Now
                </button>
              </div>
            </div>
          </div>
        )}
      </Link>

      {/* Product name & price below image */}
      <div className="mt-2 px-0.5">
        <Link to={`/product/${product.slug}`}>
          <h3 className="font-body text-[12px] font-medium text-foreground leading-snug line-clamp-2 group-hover:text-primary transition-colors duration-300">
            {product.name}
          </h3>
        </Link>
        {(hasSale || product.regular_price) && (
          <div className="flex items-baseline gap-1.5 mt-0.5">
            {hasSale ? (
              <>
                <span className="font-body font-medium text-foreground text-[12px]">
                  £{product.sale_price!.toFixed(2)}
                </span>
                <span className="font-body text-muted-foreground/60 line-through text-[10px]">
                  £{product.regular_price!.toFixed(2)}
                </span>
              </>
            ) : (
              <span className="font-body font-medium text-foreground text-[12px]">
                £{product.regular_price!.toFixed(2)}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ShopProductCard;